import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { IconeBusca, IconeCarrinho, IconeCheck, IconeFiltro, IconeRelogio } from '../../../../../../assets/svg'
import { useOrderCart } from '../hooks/useOrderCart'
import { SHAPE_FILTERS, useProducts } from '../hooks/useProducts'
import {
  BuyButton,
  CartBadge,
  CartIconContainer,
  ComingSoonBadge,
  ComingSoonButton,
  ErrorContainer,
  FilterBadge,
  FilterButton,
  FilterCheckIcon,
  FilterContainer,
  FilterDropdown,
  FilterDropdownHeader,
  FilterOption,
  HeaderActions,
  LoadingContainer,
  ProductCard,
  ProductGrid,
  ProductImage,
  ProductInfo,
  ProductListContainer,
  ProductListHeader,
  ProductName,
  ProductPrice,
  SearchContainer,
  SearchIcon,
  SearchInput
} from '../styles'
import { FilePdf } from 'phosphor-react'
import productImage from '../../../../../../assets/img/aspaec/produto-sem-imagem.jpeg'

interface ProductListProps {
  onProductAdded?: () => void
  onNavigateToCart?: () => void
}

interface ComingSoonProduct {
  id: string
  name: string
  image: string
  shapeId: number
}

const COMING_SOON_OIL_IMAGE =
  'https://s3.amazonaws.com/cannect.portal.upload.public/produtos/16062/64741394-354b-4ad0-9a4e-8a45c0627ef0-removebg-preview.png'
const COMING_SOON_FLOWER_IMAGE =
  'https://s3.amazonaws.com/cannect.portal.upload.public/produtos/16061/WhatsApp_Image_2025-09-18_at_10.00.19-removebg-preview.png'

const COMING_SOON_PRODUCTS: ComingSoonProduct[] = [
  {
    id: 'coming-soon-oil',
    name: 'Óleo Rico em CBD',
    image: COMING_SOON_OIL_IMAGE,
    shapeId: 1
  },
  {
    id: 'coming-soon-flower',
    name: 'Flor Rica em CBD',
    image: COMING_SOON_FLOWER_IMAGE,
    shapeId: 29
  }
]

export default function ProductList({ onProductAdded, onNavigateToCart }: ProductListProps) {
  const { products, loading, error, searchTerm, setSearchTerm, selectedShapeId, setSelectedShapeId } = useProducts()
  const { createOrUpdateOrder, order, fetchOrder } = useOrderCart()
  const [loadingButton, setLoadingButton] = useState<number | null>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const cartItemsCount = order?.items?.reduce((total, item) => total + item.quantity, 0) || 0
  const hasActiveFilter = selectedShapeId !== null

  const handleAddToCart = async (product: any) => {
    setLoadingButton(product.id)
    try {
      await createOrUpdateOrder([
        {
          product_id: product.id,
          quantity: 1,
          brand_id: product.brand_id,
          class_id: product.class_id,
          type: product.type,
          association_site: true,
          association_id: 1
        }
      ])

      toast.success('Produto adicionado ao carrinho')
      onProductAdded?.()
      await fetchOrder()
    } catch (err) {
      toast.error('Erro ao adicionar produto ao carrinho')
    }
    setLoadingButton(null)
  }

  const handleCartClick = () => {
    if (onNavigateToCart) {
      onNavigateToCart()
    }
  }

  const handleFilterSelect = (shapeId: number | null) => {
    setSelectedShapeId(shapeId)
    setIsFilterOpen(false)
  }

  const getActiveFilterName = () => {
    const filter = SHAPE_FILTERS.find((f) => f.id === selectedShapeId)
    return filter?.name || 'Todos'
  }

  const filteredComingSoonProducts = COMING_SOON_PRODUCTS.filter((product) => {
    if (selectedShapeId !== null && product.shapeId !== selectedShapeId) {
      return false
    }

    if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false
    }
    return true
  })

  return (
    <ProductListContainer>
      <ProductListHeader>
        <SearchContainer>
          <SearchIcon>
            <IconeBusca />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <HeaderActions>
          <FilterContainer ref={filterRef}>
            <FilterButton isActive={hasActiveFilter} onClick={() => setIsFilterOpen(!isFilterOpen)}>
              <IconeFiltro />
              <span>{hasActiveFilter ? getActiveFilterName() : 'Filtrar'}</span>
              {hasActiveFilter && <FilterBadge>1</FilterBadge>}
            </FilterButton>

            <FilterDropdown isOpen={isFilterOpen}>
              <FilterDropdownHeader>Tipo de Produto</FilterDropdownHeader>
              {SHAPE_FILTERS.map((filter) => (
                <FilterOption
                  key={filter.id ?? 'all'}
                  isSelected={selectedShapeId === filter.id}
                  onClick={() => handleFilterSelect(filter.id)}>
                  <FilterCheckIcon isVisible={selectedShapeId === filter.id}>
                    <IconeCheck />
                  </FilterCheckIcon>
                  {filter.name}
                </FilterOption>
              ))}
            </FilterDropdown>
          </FilterContainer>

          <CartIconContainer onClick={handleCartClick}>
            <IconeCarrinho width={24} height={24} />
            {cartItemsCount > 0 && <CartBadge>{cartItemsCount > 99 ? '99+' : cartItemsCount}</CartBadge>}
          </CartIconContainer>
        </HeaderActions>
      </ProductListHeader>

      {loading && <LoadingContainer>Carregando produtos...</LoadingContainer>}

      {error && <ErrorContainer>{error}</ErrorContainer>}

      {!loading && !error && products.length === 0 && filteredComingSoonProducts.length === 0 && (
        <ErrorContainer>
          {searchTerm || hasActiveFilter
            ? `Nenhum produto encontrado${searchTerm ? ` para "${searchTerm}"` : ''}${hasActiveFilter ? ` no filtro "${getActiveFilterName()}"` : ''}.`
            : 'Nenhum produto disponível no momento.'}
        </ErrorContainer>
      )}

      {!loading && !error && (products.length > 0 || filteredComingSoonProducts.length > 0) && (
        <ProductGrid>
          {products.map((product) => {
            const image = product.media.find((m) => m.format === 'IMG')?.path || productImage
            const coa = product.media.find((m) => m.format === 'PDF')?.path || ''

            const imageToShow = image ? image : COMING_SOON_PRODUCTS.find((p) => p.shapeId === product.shape.id)?.image

            return (
              <ProductCard key={product.id}>
                <ProductImage src={imageToShow} alt={product.translated_name} />
                <ProductInfo>
                  <ProductName>{product.translated_name}</ProductName>
                  <ProductPrice>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(product.price)}
                  </ProductPrice>
                  {coa && (
                    <button
                      style={{
                        background: 'none',
                        cursor: 'pointer',
                        border: '1px solid #02b0bb',
                        borderRadius: '4px',
                        color: '#02b0bb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        width: '10%'
                      }}
                      onClick={() => window.open(coa, '_blank')}>
                      <FilePdf />
                      CoA
                    </button>
                  )}
                </ProductInfo>
                <BuyButton onClick={() => handleAddToCart(product)} disabled={loadingButton !== null}>
                  {loadingButton !== null ? 'Adicionando...' : 'Comprar Agora'}
                </BuyButton>
              </ProductCard>
            )
          })}

          {filteredComingSoonProducts.map((product) => (
            <ProductCard key={product.id} isComingSoon>
              <ComingSoonBadge>Em Breve</ComingSoonBadge>
              <ProductImage src={product.image} alt={product.name} />
              <ProductInfo>
                <ProductName>{product.name}</ProductName>
                <ProductPrice style={{ color: '#6b7280' }}>Preço a definir</ProductPrice>
              </ProductInfo>
              <ComingSoonButton>
                <IconeRelogio />
                Em Breve
              </ComingSoonButton>
            </ProductCard>
          ))}
        </ProductGrid>
      )}
    </ProductListContainer>
  )
}
